import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * Top-level boundary so a thrown render error never produces a permanent
 * white screen. Shows a friendly Bootstrap card with reload + sign-out
 * actions (clearing localStorage in case MSAL cache is the cause).
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error, info);
  }

  private reload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  private hardReset = () => {
    try {
      window.localStorage.clear();
      window.sessionStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.assign('/');
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="container py-5">
        <div className="card shadow-sm mx-auto" style={{ maxWidth: 560 }}>
          <div className="card-body">
            <h1 className="h4 mb-3">Something went wrong</h1>
            <p className="text-muted">
              The page hit an unexpected error. Try reloading. If it keeps happening, use
              "Reset session" to clear local sign-in data and start over.
            </p>
            <pre className="small bg-light border rounded p-2" style={{ maxHeight: 160, overflow: 'auto' }}>
              {this.state.error.message}
            </pre>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={this.reload}>Reload</button>
              <button type="button" className="btn btn-outline-secondary" onClick={this.hardReset}>Reset session</button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
